/**
 *  Free Shipping bar - cart drawer / cart page
 *
 *  Stock behaviour (unchanged):
 *    Toggles the "remaining" vs "qualified" message based on cart total
 *    and substitutes the formatted remaining money value into the first
 *    `<span>` of `.free-shipping--text-remaining` (excluding `.free-shipping-icon`).
 *
 *  Salesforce Data Cloud (360) personalisation (additive):
 *    If the merchant has set the "Free shipping Personalisation ID" theme
 *    setting, the element will carry a `data-personalisation-id` attribute.
 *    On connection we then:
 *      1. Wait for `window.SalesforceInteractions.Personalization.fetch`
 *         to be available (up to 15s).
 *      2. Call `Personalization.fetch([personalisationId])` once per ID,
 *         caching the response so subsequent cart updates (which destroy
 *         and re-render this element) don't re-fetch.
 *      3. Apply the returned text body to whichever state is currently
 *         visible (`.free-shipping--text-remaining__body` or
 *         `.free-shipping--text-full__body`, with fallback that preserves
 *         `.free-shipping-icon`), substituting `{{amount}}` placeholders
 *         with the formatted remaining money value.
 *      4. Dispatch a bubbling `free-shipping:personalisation:ready`
 *         (or `:error`) CustomEvent and clear the inline skeleton.
 *
 *  Attribute key resolution:
 *    Tries Info_Bar_Text, Content, Text, HTML, etc., then the first
 *    non-empty string on `attributes`.
 */
const freeShippingIsShiels = () => window.STORE_CONTEXT?.isShiels === true;

/**
 *  Salesforce Data Cloud (360) measurement traces (uni2 vocabulary):
 *    - cartDrawerInfoBar_decision           (every personalisation evaluation)
 *    - cartDrawerInfoBar_view               (impression once the bar is visible)
 *    - cartDrawerInfoBar_threshold_reached  (cart total reaches/exceeds the free-shipping minimum)
 *    - cartDrawerInfoBar_click              (anchor / button click inside the bar)
 *    - cartDrawerInfoBar_control            (posture = control)
 *    - cartDrawerInfoBar_suppressed         (posture = suppressed)
 *    - cartDrawerInfoBar_hidden_no_match    (posture = no_match / hidden / no payload returned)
 *
 *  Final payload shape must be confirmed with Performance Lead before go-live.
 */
const freeShippingEmitTrace = (eventType, context = {}) => {
  if (!freeShippingIsShiels()) return;
  if (
    typeof window === "undefined" ||
    !window.SalesforceInteractions ||
    typeof window.SalesforceInteractions.sendEvent !== "function"
  ) {
    return;
  }
  const {
    personalisationId,
    contentId,
    decisionId,
    personalizationPointId,
    personalizationPointName,
    posture,
    attributes,
    source,
    href,
    cartTotal,
    cartMinimum,
    cartRemaining,
    isQualified,
  } = context;
  try {
    window.SalesforceInteractions.sendEvent({
      interaction: {
        name: `cartDrawerInfoBar_${eventType}`,
        eventType: "userEngagement",
        engagementType: eventType,
        source: source || "free_shipping",
        personalization: contentId ? { id: contentId, contentId: contentId } : undefined,
        personalizationPointName,
        personalizationPointId,
        contentId,
        decisionId,
        posture,
        href,
        cartTotal,
        cartMinimum,
        cartRemaining,
        isQualified,
        attributes: attributes || {},
      },
    });
    console.log("[free-shipping] Trace", eventType, context);
  } catch (e) {
    console.warn("[free-shipping] Trace failed", eventType, e);
  }
};

const freeShippingResolvePosture = (attributes) => {
  if (!attributes) return "active";
  const raw =
    attributes.Posture ||
    attributes.posture ||
    attributes.Variant ||
    attributes.Decision;
  if (!raw) return "active";
  const v = String(raw).toLowerCase().replace(/[\s\-]+/g, "_");
  if (v === "active" || v === "variant" || v === "show") return "active";
  if (v === "control" || v === "holdout") return "control";
  if (v === "suppressed" || v === "suppress") return "suppressed";
  if (v === "no_match" || v === "nomatch" || v === "hidden" || v === "hide") {
    return "no_match";
  }
  return "active";
};

if (!customElements.get("free-shipping")) {
  const personalisationCache = new Map();
  const SDK_MAX_WAIT_MS = 15000;
  const SDK_POLL_INTERVAL_MS = 100;
  const SKELETON_TIMEOUT_MS = 6000;

  const AMOUNT_PLACEHOLDER = /\{\{\s*amount\s*\}\}|\{\s*amount\s*\}/gi;

  const KNOWN_TEXT_KEYS = [
    "Info_Bar_Text",
    "InfoBarText",
    "Content",
    "Text",
    "HTML",
    "Body",
    "Message",
    "info_bar_text",
    "content",
    "text",
    "html",
    "body",
    "message",
  ];

  const extractPersonalisedHtml = (attributes) => {
    if (!attributes || typeof attributes !== "object") return null;
    for (const key of KNOWN_TEXT_KEYS) {
      const value = attributes[key];
      if (typeof value === "string" && value.trim().length > 0) {
        return { value, key };
      }
    }
    for (const [key, value] of Object.entries(attributes)) {
      if (typeof value === "string" && value.trim().length > 0) {
        return { value, key };
      }
    }
    return null;
  };

  const sdkReady = () =>
    typeof window !== "undefined" &&
    window.SalesforceInteractions &&
    window.SalesforceInteractions.Personalization &&
    typeof window.SalesforceInteractions.Personalization.fetch === "function";

  const resolveSdk = () =>
    new Promise((resolve, reject) => {
      if (sdkReady()) return resolve(window.SalesforceInteractions);
      const started = Date.now();
      const interval = setInterval(() => {
        if (sdkReady()) {
          clearInterval(interval);
          resolve(window.SalesforceInteractions);
        } else if (Date.now() - started > SDK_MAX_WAIT_MS) {
          clearInterval(interval);
          reject(new Error("Salesforce Data Cloud SDK / Personalization.fetch not available"));
        }
      }, SDK_POLL_INTERVAL_MS);
    });

  const fetchPersonalisation = (personalisationId) => {
    if (personalisationCache.has(personalisationId)) {
      return personalisationCache.get(personalisationId);
    }
    const promise = resolveSdk()
      .then((SalesforceInteractions) => {
        console.log("[free-shipping] Fetching personalisation for", personalisationId);
        return SalesforceInteractions.Personalization.fetch([personalisationId]);
      })
      .then((response) => {
        const personalizations = (response && response.personalizations) || [];
        const match = personalizations.find(
          (p) => p && p.personalizationPointName === personalisationId
        );
        if (!match) {
          throw new Error(`No personalisation returned for "${personalisationId}"`);
        }
        return match;
      });
    personalisationCache.set(personalisationId, promise);
    promise.catch(() => personalisationCache.delete(personalisationId));
    return promise;
  };

  class FreeShipping extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      let amount_text = this.querySelector(
        ".free-shipping--text-remaining span:not(.free-shipping-icon)"
      );
      let total = parseInt(this.dataset.cartTotal, 10);
      let minimum = Math.round(parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1));
      let percentage = 1;
      this.remainingText = this.querySelector(".free-shipping--text-remaining");
      this.fullText = this.querySelector(".free-shipping--text-full");
      if (total < minimum) {
        percentage = total / minimum;

        if (amount_text) {
          let remaining = minimum - total,
            format = window.theme.settings.money_with_currency_format || "${{amount}}";
          amount_text.innerHTML = formatMoney(remaining, format);
        }
        this.remainingText.style.display = "block";
        this.fullText.style.display = "none";
      } else {
        this.remainingText.style.display = "none";
        this.fullText.style.display = "block";
      }

      this.style.setProperty("--percentage", percentage);

      const personalisationId = this.dataset.personalisationId;
      if (freeShippingIsShiels() && personalisationId) {
        const remaining = Math.max(0, minimum - total);
        const moneyFormat =
          (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) ||
          "${{amount}}";
        this.applyPersonalisation(personalisationId, {
          remaining,
          minimum,
          total,
          formattedRemaining: formatMoney(remaining, moneyFormat),
          isQualified: total >= minimum,
        });
      }
    }

    /**
     * Applies personalised HTML to the active message container.
     * Supports Comet `__body` wrappers and the theme's flat markup (truck icon + text).
     */
    applyPersonalisedContent(renderedHtml, isQualified) {
      const targetSelector = isQualified
        ? ".free-shipping--text-full__body"
        : ".free-shipping--text-remaining__body";
      const containerSelector = isQualified
        ? ".free-shipping--text-full"
        : ".free-shipping--text-remaining";

      const targetBody = this.querySelector(targetSelector);
      if (targetBody) {
        targetBody.innerHTML = renderedHtml;
        return true;
      }

      const container = this.querySelector(containerSelector);
      if (!container) return false;

      const icon = container.querySelector(".free-shipping-icon");
      if (icon) {
        Array.from(container.childNodes).forEach((node) => {
          if (node !== icon) container.removeChild(node);
        });
        container.appendChild(document.createRange().createContextualFragment(renderedHtml));
      } else {
        container.innerHTML = renderedHtml;
      }
      return true;
    }

    applyPersonalisation(personalisationId, ctx) {
      const skeletonTimeout = setTimeout(() => this.clearSkeleton(), SKELETON_TIMEOUT_MS);

      fetchPersonalisation(personalisationId)
        .then((personalization) => {
          clearTimeout(skeletonTimeout);

          const attributes = (personalization && personalization.attributes) || {};
          const resolved = extractPersonalisedHtml(attributes);
          const posture = freeShippingResolvePosture(attributes);

          console.log("[free-shipping] Personalisation result", {
            personalisationId,
            attributes,
            personalization,
            isQualified: ctx.isQualified,
            posture,
            usedAttributeKey: resolved && resolved.key,
          });

          const contentId =
            personalization.personalizationId ||
            attributes.contentId ||
            attributes.ContentId ||
            null;
          if (contentId) this.dataset.personalisationContentId = contentId;
          if (personalization.decisionId) this.dataset.personalisationDecisionId = personalization.decisionId;
          if (personalization.personalizationPointId) {
            this.dataset.personalisationPointId = personalization.personalizationPointId;
          }
          if (personalization.personalizationPointName) {
            this.dataset.personalisationPointName = personalization.personalizationPointName;
          }
          this.dataset.personalisationPosture = posture;

          const traceContext = {
            personalisationId,
            contentId,
            decisionId: personalization.decisionId,
            personalizationPointId: personalization.personalizationPointId,
            personalizationPointName: personalization.personalizationPointName,
            posture,
            attributes,
            cartTotal: ctx.total,
            cartMinimum: ctx.minimum,
            cartRemaining: ctx.remaining,
            isQualified: ctx.isQualified,
          };
          this._freeShippingTraceContext = traceContext;

          freeShippingEmitTrace("decision", traceContext);

          if (posture === "control") {
            freeShippingEmitTrace("control", traceContext);
            this.clearSkeleton();
            return;
          }
          if (posture === "suppressed") {
            freeShippingEmitTrace("suppressed", traceContext);
            this.classList.add("free-shipping--suppressed");
            this.style.display = "none";
            this.clearSkeleton();
            return;
          }
          if (posture === "no_match") {
            freeShippingEmitTrace("hidden_no_match", traceContext);
            this.clearSkeleton();
            return;
          }

          if (resolved) {
            const rendered = resolved.value.replace(AMOUNT_PLACEHOLDER, ctx.formattedRemaining);
            if (!this.applyPersonalisedContent(rendered, ctx.isQualified)) {
              console.warn(
                "[free-shipping] Could not find target container for personalised content",
                ctx.isQualified ? ".free-shipping--text-full" : ".free-shipping--text-remaining"
              );
            }
          } else {
            console.warn(
              "[free-shipping] Personalisation returned no usable string attribute - leaving merchant default in place",
              attributes
            );
          }

          this.clearSkeleton();

          this.armViewTrace();
          this.armClickTrace();
          if (ctx.isQualified) {
            freeShippingEmitTrace("threshold_reached", {
              ...traceContext,
              source: "initial_render",
            });
          }

          this.dispatchEvent(
            new CustomEvent("free-shipping:personalisation:ready", {
              bubbles: true,
              detail: {
                personalisationId,
                contentId,
                decisionId: personalization.decisionId,
                personalizationPointId: personalization.personalizationPointId,
                personalizationPointName: personalization.personalizationPointName,
                posture,
                attributes,
                personalization,
                appliedTo: ctx.isQualified ? "full" : "remaining",
                usedAttributeKey: resolved && resolved.key,
              },
            })
          );
        })
        .catch((err) => {
          clearTimeout(skeletonTimeout);
          console.warn("[free-shipping] Failed to fetch personalisation", personalisationId, err);
          const errorContext = {
            personalisationId,
            personalizationPointName: personalisationId,
            posture: "no_match",
            cartTotal: ctx.total,
            cartMinimum: ctx.minimum,
            cartRemaining: ctx.remaining,
            isQualified: ctx.isQualified,
            source: "fetch_error",
          };
          freeShippingEmitTrace("decision", errorContext);
          freeShippingEmitTrace("hidden_no_match", errorContext);
          this.clearSkeleton();
          this.dispatchEvent(
            new CustomEvent("free-shipping:personalisation:error", {
              bubbles: true,
              detail: { personalisationId, error: err },
            })
          );
        });
    }

    armViewTrace() {
      if (this._freeShippingViewArmed) return;
      this._freeShippingViewArmed = true;
      const fire = (source) => {
        const ctx = this._freeShippingTraceContext;
        if (!ctx) return;
        freeShippingEmitTrace("view", { ...ctx, source: source || "viewport" });
      };
      if (typeof IntersectionObserver === "function") {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (this._freeShippingViewFired) return;
              this._freeShippingViewFired = true;
              fire("viewport");
            });
          },
          { threshold: 0.5 }
        );
        observer.observe(this);
      } else {
        fire("fallback");
      }
    }

    armClickTrace() {
      if (this._freeShippingClickArmed) return;
      this._freeShippingClickArmed = true;
      this.addEventListener("click", (e) => {
        const ctx = this._freeShippingTraceContext;
        if (!ctx) return;
        const clickEl = e.target && e.target.closest("a, button, [role='button']");
        if (!clickEl || !this.contains(clickEl)) return;
        const href = clickEl.getAttribute("href") || undefined;
        freeShippingEmitTrace("click", { ...ctx, source: "bar_click", href });
      });
    }

    clearSkeleton() {
      if (!this.classList.contains("free-shipping--personalising")) return;
      this.classList.remove("free-shipping--personalising");
      const skeleton = this.querySelector(".free-shipping--skeleton");
      if (skeleton) setTimeout(() => skeleton.remove(), 400);
    }
  }
  customElements.define("free-shipping", FreeShipping);
}
